import { useState } from 'react';
import { useApp } from '@context/AppContext';
import { saveCanvas } from '@utils/canvasSaveLoad';
import {
  Lock, Unlock, Trash2, UserMinus, Copy, Check, X, AlertTriangle, Gamepad2, StopCircle,
  FilePlus2, Image as ImageIcon, Save,
} from 'lucide-react';

const BG_OPTIONS = [
  { label: 'Paper', colour: '#FAF8F5' },
  { label: 'White', colour: '#FFFFFF' },
  { label: 'Grey', colour: '#D1D5DB' },
  { label: 'Dark', colour: '#1F2937' },
  { label: 'Black', colour: '#0A0A0A' },
];

export default function HostPanel({ onClearCanvas, onKickUser, onToggleLock, onSetBackground, onOpenGameSelector, onEndGame, onOpenGallery, stageRef }) {
  const { state, dispatch } = useApp();
  const { room, user, canvas, game } = state;
  const [confirmClear, setConfirmClear] = useState(false);
  const [savingCanvas, setSavingCanvas] = useState(false);
  const [copied, setCopied] = useState(false);

  const hasStrokes = (canvas.allStrokes || []).length > 0;

  const members = Object.entries(room.members || {}).sort(
    ([, a], [, b]) => a.joinedAt - b.joinedAt
  );

  const handleCopyCode = async () => {
    const text = `Join me on DrawLab! Room code: ${room.code} — https://ddd-draw-lab.web.app/app/room/${room.code}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Ignore
    }
  };

  const handleClear = () => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    onClearCanvas();
    setConfirmClear(false);
    dispatch({ type: 'TOGGLE_HOST_PANEL' });
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-charcoal font-display">Host Controls</h2>
          <button
            onClick={() => dispatch({ type: 'TOGGLE_HOST_PANEL' })}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Game Mode */}
          {(!game.mode || game.phase === 'off') ? (
            <button
              onClick={() => {
                dispatch({ type: 'TOGGLE_HOST_PANEL' });
                onOpenGameSelector?.();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-amber/30 bg-amber/5 hover:bg-amber/10 transition-colors"
            >
              <Gamepad2 className="w-5 h-5 text-amber" />
              <div className="text-left">
                <p className="text-sm font-medium text-charcoal font-display">Start a Game</p>
                <p className="text-xs text-slate-400">Challenge or Relay mode</p>
              </div>
            </button>
          ) : (
            <button
              onClick={() => {
                onEndGame?.();
                dispatch({ type: 'TOGGLE_HOST_PANEL' });
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 transition-colors"
            >
              <StopCircle className="w-5 h-5 text-red-500" />
              <div className="text-left">
                <p className="text-sm font-medium text-red-600 font-display">End Game</p>
                <p className="text-xs text-slate-400">Return to free draw</p>
              </div>
            </button>
          )}

          {/* Lock/Unlock */}
          <button
            onClick={onToggleLock}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:border-amber/30 hover:bg-amber/5 transition-colors"
          >
            {room.locked ? (
              <Lock className="w-5 h-5 text-amber" />
            ) : (
              <Unlock className="w-5 h-5 text-slate-400" />
            )}
            <div className="text-left">
              <p className="text-sm font-medium text-charcoal font-display">
                {room.locked ? 'Unlock Room' : 'Lock Room'}
              </p>
              <p className="text-xs text-slate-400">
                {room.locked ? 'Allow new members to join' : 'Prevent new members from joining'}
              </p>
            </div>
          </button>

          {/* Canvas background */}
          <div className="px-4 py-3 rounded-xl border border-slate-200">
            <p className="text-sm font-medium text-charcoal font-display mb-2">Canvas Background</p>
            <div className="flex gap-2">
              {BG_OPTIONS.map(({ label, colour }) => (
                <button
                  key={colour}
                  onClick={() => onSetBackground(colour)}
                  title={label}
                  className={`w-9 h-9 rounded-lg border-2 transition-all ${
                    canvas.background === colour
                      ? 'border-amber scale-110 shadow-sm'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  style={{ backgroundColor: colour }}
                />
              ))}
            </div>
          </div>

          {/* Save & Gallery */}
          <div className="space-y-1.5">
            {/* Save canvas (full stroke data — resumable) */}
            {hasStrokes && !confirmClear && (
              <button
                onClick={async () => {
                  setSavingCanvas(true);
                  const result = await saveCanvas({
                    roomCode: room.code,
                    strokes: canvas.allStrokes,
                    background: canvas.background,
                    userId: user.id,
                    nickname: user.nickname,
                    stageRef,
                  });
                  setSavingCanvas(false);
                  if (result.success) {
                    dispatch({ type: 'ADD_TOAST', payload: { message: 'Canvas saved — load it back anytime from the Gallery' } });
                  } else {
                    dispatch({ type: 'ADD_TOAST', payload: { message: result.error || 'Failed to save' } });
                  }
                }}
                disabled={savingCanvas}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-amber/30 bg-amber/5 hover:bg-amber/10 transition-colors disabled:opacity-50"
              >
                <Save className="w-5 h-5 text-amber" />
                <div className="text-left">
                  <p className="text-sm font-medium text-charcoal font-display">
                    {savingCanvas ? 'Saving...' : 'Save Canvas'}
                  </p>
                  <p className="text-xs text-slate-400">Save all strokes — resume drawing later</p>
                </div>
              </button>
            )}

            {/* Save snapshot to Gallery */}
            {hasStrokes && onOpenGallery && !confirmClear && (
              <button
                onClick={() => {
                  onOpenGallery();
                  dispatch({ type: 'TOGGLE_HOST_PANEL' });
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:border-amber/30 hover:bg-amber/5 transition-colors"
              >
                <ImageIcon className="w-5 h-5 text-slate-400" />
                <div className="text-left">
                  <p className="text-sm font-medium text-charcoal font-display">Open Gallery</p>
                  <p className="text-xs text-slate-400">Snapshots and saved canvases</p>
                </div>
              </button>
            )}

            {/* New Canvas / Clear */}
            <button
              onClick={handleClear}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
                confirmClear
                  ? 'border-red-300 bg-red-50 hover:bg-red-100'
                  : 'border-slate-200 hover:border-red-200 hover:bg-red-50'
              }`}
            >
              {confirmClear ? (
                <AlertTriangle className="w-5 h-5 text-red-500" />
              ) : (
                <FilePlus2 className="w-5 h-5 text-slate-400" />
              )}
              <div className="text-left">
                <p className={`text-sm font-medium font-display ${confirmClear ? 'text-red-600' : 'text-charcoal'}`}>
                  {confirmClear ? 'Tap again to confirm' : 'Start New Canvas'}
                </p>
                <p className="text-xs text-slate-400">
                  {confirmClear
                    ? 'This will clear all drawings permanently'
                    : hasStrokes
                      ? 'Clear the canvas and start fresh'
                      : 'Canvas is already empty'}
                </p>
              </div>
            </button>
          </div>

          {/* Copy room code */}
          <button
            onClick={handleCopyCode}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:border-amber/30 hover:bg-amber/5 transition-colors"
          >
            {copied ? (
              <Check className="w-5 h-5 text-green-500" />
            ) : (
              <Copy className="w-5 h-5 text-slate-400" />
            )}
            <div className="text-left">
              <p className="text-sm font-medium text-charcoal font-display">
                {copied ? 'Copied!' : 'Copy Room Code'}
              </p>
              <p className="text-xs text-amber font-mono">{room.code}</p>
            </div>
          </button>

          {/* Member list with kick */}
          <div>
            <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-2">
              Members ({members.length}/8)
            </p>
            <div className="space-y-1">
              {members.map(([id, member]) => (
                <div
                  key={id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50"
                >
                  <div
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: member.colour }}
                  />
                  <span className="text-sm text-charcoal font-display truncate flex-1">
                    {member.nickname}
                  </span>
                  {id === user.id ? (
                    <span className="text-[10px] text-slate-400 font-mono">you</span>
                  ) : (
                    <button
                      onClick={() => onKickUser(id)}
                      title={`Remove ${member.nickname}`}
                      className="w-7 h-7 flex items-center justify-center rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <UserMinus className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
